import { useMemo } from 'react';
import type { INewsItem } from '../../../interfaces/News.interface';
import type { INewsCardData } from './types';
import type { ICompany } from './CompanyTag';
import type { NewsAEMImages } from '../../../interfaces/NewsAEM.interface';

export interface NormalizedNewsData {
  id: string;
  title: string;
  bannerLink?: string | null;
  content?: string;
  sourceName?: string;
  sourceLogo?: string;
  timestamp?: string | number;
  companies?: ICompany[];
  tags?: string[];
}

/**
 * Type guard to check if newsItem is INewsItem
 */
const isINewsItem = (item: INewsItem | INewsCardData): item is INewsItem => {
  return 'image' in item || 'body' in item || 'source' in item;
};

/**
 * Hook to normalize news data from either INewsItem or INewsCardData format
 */
export const useNewsCardData = (
  newsItem: INewsItem | INewsCardData,
  aemImages?: NewsAEMImages
): NormalizedNewsData => {
  return useMemo(() => {
    if (!newsItem) {
      return {
        id: "",
        title: "",
        bannerLink: null,
        content: "",
        sourceName: undefined,
        sourceLogo: undefined,
        timestamp: undefined,
        companies: undefined,
        tags: undefined,
      };
    }

    if (isINewsItem(newsItem)) {
      const bannerLink = newsItem.image;
      const content = newsItem.body;
      const sourceName = newsItem.source?.name ?? '';
      const defaultSourceLogo = aemImages?.defaultSourceLogo ?? '';
      const sourceLogo = newsItem.source?.avatar ?? newsItem.source?.icon ?? defaultSourceLogo;
      const timestamp = newsItem.timestamp ?? newsItem.time;

      // Parse companies from INewsItem
    const parsePercentage = (change?: string): number => {
      if (!change) return 0;
      const cleaned = change.replace(/[+%]/g, '');
        const parsed = parseFloat(cleaned);
        return Number.isNaN(parsed) ? 0 : parsed;
    };

    let companies: ICompany[] | undefined;
      if (newsItem.stockName) {
        companies = [
          {
            symbol: newsItem.stockName,
            percentage: parsePercentage(newsItem.change),
          },
        ];
      }

      // Safely extract tags with proper type narrowing
      const tags: string[] | undefined = 
        'tags' in newsItem && Array.isArray(newsItem.tags) 
          ? newsItem.tags 
          : undefined;

    return {
      id: newsItem.id,
      title: newsItem.title,
      bannerLink,
      content,
      sourceName,
      sourceLogo,
      timestamp,
      companies,
      tags,
      };
    }

    // Handle INewsCardData
    return {
      id: newsItem.id,
      title: newsItem.title,
      bannerLink: newsItem.banner_link,
      content: newsItem.content,
      sourceName: newsItem.more_info?.source_name ?? '',
      sourceLogo: newsItem.more_info?.source_logo ?? aemImages?.defaultSourceLogo ?? '',
      timestamp: newsItem.created_at,
      companies: newsItem.companies,
      tags: newsItem.tags,
    };
  }, [newsItem]);
};

